import React from 'react'
import { Sidebar, Text, Heading } from '../../components/ui'
import { PermissionGuard } from '../../components/PermissionGuard'
import { crmService } from '../../services/crm'
import { CRMStage, CRMOpportunity } from '../../types/crm'
import {
  DndContext,
  closestCenter,
  useSensor,
  useSensors,
  PointerSensor,
  DragEndEvent
} from '@dnd-kit/core'
import {
  SortableContext,
  rectSortingStrategy,
} from '@dnd-kit/sortable'

const CRMOportunidadesKanban: React.FC = () => {
  // Paleta de cores por coluna/estágio
  const stageColors: Record<string, { container: string; border: string; headerText: string; accent: string }> = {
    lead: {
      container: 'bg-blue-50 dark:bg-blue-900/20',
      border: 'border-blue-200 dark:border-blue-800',
      headerText: 'text-blue-700 dark:text-blue-300',
      accent: 'bg-blue-300 dark:bg-blue-600'
    },
    proposta: {
      container: 'bg-amber-50 dark:bg-amber-900/20',
      border: 'border-amber-200 dark:border-amber-800',
      headerText: 'text-amber-700 dark:text-amber-300',
      accent: 'bg-amber-300 dark:bg-amber-600'
    },
    negociacao: {
      container: 'bg-purple-50 dark:bg-purple-900/20',
      border: 'border-purple-200 dark:border-purple-800',
      headerText: 'text-purple-700 dark:text-purple-300',
      accent: 'bg-purple-300 dark:bg-purple-600'
    },
    fechado_perdido: {
      container: 'bg-red-50 dark:bg-red-900/20',
      border: 'border-red-200 dark:border-red-800',
      headerText: 'text-red-700 dark:text-red-300',
      accent: 'bg-red-300 dark:bg-red-600'
    },
    fechado_ganho: {
      container: 'bg-green-50 dark:bg-green-900/20',
      border: 'border-green-200 dark:border-green-800',
      headerText: 'text-green-700 dark:text-green-300',
      accent: 'bg-green-300 dark:bg-green-600'
    },
  }

  const [stages, setStages] = React.useState<CRMStage[]>([])
  const [byStage, setByStage] = React.useState<Record<string, CRMOpportunity[]>>({})
  const [loading, setLoading] = React.useState<boolean>(true)
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }))

  const load = React.useCallback(async () => {
    setLoading(true)
    const s = await crmService.getStages()
    const entries = await Promise.all(s.map(async (st) => {
      const items = await crmService.listOpportunitiesByStage(st.id)
      return [st.id, items] as [string, CRMOpportunity[]]
    }))
    const dict: Record<string, CRMOpportunity[]> = {}
    entries.forEach(([id, items]) => { dict[id] = items })
    setStages(s)
    setByStage(dict)
    setLoading(false)
  }, [])

  React.useEffect(() => { load() }, [load])

  const onDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event
    if (!over) return
    const [oppId, fromStageId] = (active.id as string).split('|')
    const toStageId = over.id as string
    if (fromStageId === toStageId) return

    // Otimista
    setByStage((prev) => {
      const next = { ...prev }
      const item = next[fromStageId]?.find(i => i.id === oppId)
      if (!item) return prev
      next[fromStageId] = (next[fromStageId] || []).filter(i => i.id !== oppId)
      next[toStageId] = [{ ...item, stage_id: toStageId }, ...(next[toStageId] || [])]
      return next
    })

    try {
      await crmService.updateOpportunityStage(oppId, toStageId)
    } catch {
      // Recarregar em caso de erro
      await load()
    }
  }

  return (
    <div className="flex h-screen bg-gray-100 dark:bg-gray-900">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-x-hidden overflow-y-auto">
        <PermissionGuard moduleName="crm_oportunidades" permissionType="show">
          <div className="py-6 px-4 sm:px-6 lg:px-8">
            <div className="mb-6">
              <Heading as="h1" size="3xl" className="text-gray-900 dark:text-white">Oportunidades</Heading>
              <Text size="sm" color="muted" className="mt-2">Visualização Kanban do funil de vendas</Text>
            </div>
            {loading ? (
              <Text size="sm" color="muted">Carregando...</Text>
            ) : (
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
                  {stages.map((stage) => (
                    <div
                      key={stage.id}
                      className={`rounded-lg p-3 border ${stageColors[stage.key]?.container || 'bg-gray-50 dark:bg-gray-900/30'} ${stageColors[stage.key]?.border || 'border-gray-200 dark:border-gray-700'}`}
                    >
                      <div className={`h-1 w-full rounded ${stageColors[stage.key]?.accent || 'bg-gray-300 dark:bg-gray-600'} mb-2`} />
                      <div className="flex items-center justify-between mb-2">
                        <Text size="sm" className={`font-semibold ${stageColors[stage.key]?.headerText || 'text-gray-900 dark:text-white'}`}>
                          {stage.name}
                        </Text>
                        <span className={`text-xs ${stageColors[stage.key]?.headerText || 'text-gray-500 dark:text-gray-400'}`}>
                          {(byStage[stage.id] || []).length}
                        </span>
                      </div>
                      <SortableContext items={(byStage[stage.id] || []).map(o => `${o.id}|${stage.id}`)} strategy={rectSortingStrategy}>
                        <div
                          id={stage.id}
                          className="space-y-2 min-h-[120px]"
                        >
                          {(byStage[stage.id] || []).map((opp) => (
                            <div
                              key={`${opp.id}|${stage.id}`}
                              id={`${opp.id}|${stage.id}`}
                              className="bg-white dark:bg-gray-800 rounded-md shadow p-3"
                            >
                              <div className="text-sm text-gray-900 dark:text-white font-medium">{opp.title}</div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                R$ {(opp.value_cents / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                              </div>
                              {opp.company?.name && (
                                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">{opp.company.name}</div>
                              )}
                            </div>
                          ))}
                        </div>
                      </SortableContext>
                    </div>
                  ))}
                </div>
              </DndContext>
            )}
          </div>
        </PermissionGuard>
      </div>
    </div>
  )
}

export default CRMOportunidadesKanban
